import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { getCaseSize, casesToUnits, isCaseAligned } from '@theme/case-size';

/** @typedef {import('./utilities').TextComponent} TextComponent */

// Delay before running automatic cart corrections, letting initial rendering settle
const CART_AUTOMATION_DELAY = 500;

// Module-level lock shared by all cart-items components (drawer + cart page),
// so only one automatic cart mutation runs at a time.
let cartAutomationInFlight = false;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement | undefined} caseNotice - Notice container for automatic cart corrections.
 * @property {HTMLElement | undefined} caseNoticeText - Notice text element.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {string[]} Messages to show once the current cart re-render settles. */
  #pendingCartNotices = [];

  /** @type {number | undefined} */
  #cartAutomationTimeout;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#scheduleCartAutomations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    if (this.#cartAutomationTimeout) clearTimeout(this.#cartAutomationTimeout);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Case-pack selectors emit case counts; the cart API expects units
    const caseSize = getCaseSize(/** @type {Element} */ (event.target));

    this.updateQuantity({
      line,
      quantity: casesToUnits(quantity, caseSize),
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Finds a cart item row by its cart line number.
   * @param {number} line - The 1-based cart line number.
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRow(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.cartLine === String(line));
  }

  /**
   * Schedules a pass of automatic cart corrections (see `#runCartAutomations`).
   */
  #scheduleCartAutomations() {
    if (this.#cartAutomationTimeout) clearTimeout(this.#cartAutomationTimeout);
    this.#cartAutomationTimeout = setTimeout(() => this.#runCartAutomations(), CART_AUTOMATION_DELAY);
  }

  /**
   * Runs at most one automatic cart mutation per pass, in a fixed order. The
   * next pass is scheduled after the mutation succeeds, against fresh server
   * markup. Notices are shown once nothing is left to mutate.
   */
  #runCartAutomations() {
    if (cartAutomationInFlight) return;
    if (this.#autoCorrectCaseLines()) return;
    if (this.#removeGiftWrapIfNeeded()) return;
    this.#flushCartNotices();
  }

  /**
   * Auto-corrects the first case-pack line whose unit quantity is not a whole
   * number of cases (e.g. added via API or clamped by Shopify stock limits) by
   * rounding it DOWN to whole cases (0 removes the line) and notifies the
   * customer. Targets the line by its stable `item.key`, not the line number.
   * @returns {boolean} Whether a correction request was started.
   */
  #autoCorrectCaseLines() {
    const misalignedRow = this.refs.cartItemRows?.find((row) => {
      const caseSize = parseInt(row.dataset.caseSize || '1', 10);
      const units = parseInt(row.dataset.lineUnits || '0', 10);
      return caseSize > 1 && units > 0 && !isCaseAligned(units, caseSize);
    });

    if (!misalignedRow) return false;

    const caseSize = parseInt(misalignedRow.dataset.caseSize || '1', 10);
    const units = parseInt(misalignedRow.dataset.lineUnits || '0', 10);
    const line = parseInt(misalignedRow.dataset.cartLine || '0', 10);
    const key = misalignedRow.dataset.key;
    const title = misalignedRow.dataset.caseProductTitle || '';
    const correctedUnits = Math.floor(units / caseSize) * caseSize;

    if (!key || !line) return false;

    const notice = this.#buildCaseNotice(title, caseSize, correctedUnits === 0);
    const onSuccess = () => this.#queueCartNotice(notice);

    cartAutomationInFlight = true;
    const releaseLock = () => {
      cartAutomationInFlight = false;
    };

    if (correctedUnits === 0) {
      this.#animateLineRemoval(line);
      this.updateQuantity({ line, key, quantity: 0, action: 'clear', onSuccess, onSettled: releaseLock });
    } else {
      this.updateQuantity({ line, key, quantity: correctedUnits, action: 'change', onSuccess, onSettled: releaseLock });
    }

    return true;
  }

  /**
   * Removes the gift wrapping line when Liquid marks it for removal
   * (presentation boxes cover every wrappable item). The decision is made on the
   * server; the attribute disappears once the line is gone.
   * @returns {boolean} Whether a removal request was started.
   */
  #removeGiftWrapIfNeeded() {
    const wrapper = /** @type {HTMLElement | null} */ (this.querySelector('[data-remove-gift-wrap-key]'));
    const key = wrapper?.dataset.removeGiftWrapKey;
    if (!wrapper || !key) return false;

    const row = this.refs.cartItemRows?.find((cartRow) => cartRow.dataset.key === key);
    const line = parseInt(row?.dataset.cartLine || '0', 10);

    cartAutomationInFlight = true;
    if (line) this.#animateLineRemoval(line);
    this.updateQuantity({
      line,
      key,
      quantity: 0,
      action: 'clear',
      onSuccess: () => this.querySelector('[ref="giftWrapNotice"]')?.classList.remove('hidden'),
      onSettled: () => {
        cartAutomationInFlight = false;
      },
    });

    return true;
  }

  /**
   * Builds a customer-facing notice about a corrected case-pack line.
   * @param {string} title - The product title.
   * @param {number} caseSize - The case size.
   * @param {boolean} removed - Whether the line was removed entirely.
   * @returns {string | undefined} The notice text.
   */
  #buildCaseNotice(title, caseSize, removed) {
    const wrapper = /** @type {HTMLElement | null} */ (this.querySelector('[data-case-adjusted-message]'));
    const template = removed ? wrapper?.dataset.caseRemovedMessage : wrapper?.dataset.caseAdjustedMessage;

    return template?.replace('__TITLE__', title).replace('__COUNT__', caseSize.toString());
  }

  /**
   * Queues a notice to show once automatic cart corrections settle.
   * @param {string | undefined} message - The notice text.
   */
  #queueCartNotice(message) {
    if (!message) return;

    this.#pendingCartNotices.push(message);
  }

  /**
   * Displays queued automatic correction notices in the cart notice area.
   */
  #flushCartNotices() {
    if (this.#pendingCartNotices.length === 0) return;

    const notice = /** @type {HTMLElement | null} */ (this.querySelector('[ref="caseNotice"]'));
    const noticeText = /** @type {HTMLElement | null} */ (this.querySelector('[ref="caseNoticeText"]'));
    if (!notice || !noticeText) return;

    noticeText.textContent = this.#pendingCartNotices.join(' ');
    notice.classList.remove('hidden');
    this.#pendingCartNotices = [];
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    this.#animateLineRemoval(line);
  }

  /**
   * Animates the removal of a cart line row (and its nested lines).
   * @param {number} line - The 1-based cart line number.
   */
  #animateLineRemoval(line) {
    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.key] - The stable line item key; when provided the
   *   change targets it instead of the positional line number (safe against
   *   concurrent cart mutations shifting line numbers).
   * @param {() => void} [config.onSuccess] - Called after the cart change succeeds and the section is morphed.
   * @param {() => void} [config.onSettled] - Called when the request settles (success or error).
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, key, onSuccess, onSettled } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(key ? { id: key } : { line: line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();

        onSuccess?.();

        // Re-run automatic corrections against the fresh markup and surface notices
        this.#scheduleCartAutomations();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        onSettled?.();
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const row = this.#getCartItemRow(line);
    const quantitySelector = row?.querySelector('[ref="quantitySelectors[]"]');
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      this.#renderSectionThenScheduleAutomations();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#scheduleCartAutomations();
    } else {
      this.#renderSectionThenScheduleAutomations();
    }
  };

  /**
   * Re-renders the section and schedules automatic corrections only once the
   * fresh markup is in place, so they never read the previous cart state.
   */
  #renderSectionThenScheduleAutomations() {
    sectionRenderer
      .renderSection(this.sectionId, { cache: false })
      .then(() => this.#scheduleCartAutomations())
      .catch((error) => console.error(error));
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        // Case-pack selectors track quantities in cases, not units
        const caseSize = getCaseSize(selector);
        const cartQuantity = caseSize > 1 ? Math.floor(item.quantity / caseSize) : item.quantity;

        input.setAttribute('data-cart-quantity', cartQuantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
